import React from 'react';
import { Phone, QrCode, Scan, ChevronDown, ChevronUp, X } from 'lucide-react';

interface MobileQRSectionProps {
  formData: {
    mobile: string;
    mobile2: string;
    mobile3: string;
    qrCodeData: string;
  };
  showAdditionalMobiles: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus: (fieldName: string) => void;
  onBlur: (fieldName: string) => void;
  getPlaceholder: (fieldName: string, defaultPlaceholder: string) => string;
  onToggleAdditionalMobiles: () => void;
  onStartQRScanner: () => void;
  onClearQRData: () => void;
}

export const MobileQRSection: React.FC<MobileQRSectionProps> = ({
  formData,
  showAdditionalMobiles,
  onChange,
  onFocus,
  onBlur,
  getPlaceholder,
  onToggleAdditionalMobiles,
  onStartQRScanner,
  onClearQRData
}) => {
  return (
    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
      <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center space-x-2">
        <Phone className="w-5 h-5 text-purple-600" />
        <span>Mobile and QR</span>
      </h3>
      
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Mobile Number</label>
          <input
            type="tel"
            name="mobile"
            value={formData.mobile}
            onChange={onChange}
            onFocus={() => onFocus('mobile')}
            onBlur={() => onBlur('mobile')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder={getPlaceholder('mobile', 'Enter mobile number')}
          />
        </div>

        {/* Additional Mobile Numbers (Expandable) */}
        <div className="space-y-3">
          <button
            type="button"
            onClick={onToggleAdditionalMobiles}
            className="flex items-center space-x-2 text-blue-600 hover:text-blue-700 transition-colors"
          >
            <Phone className="w-4 h-4" />
            <span className="text-sm font-medium">Additional Mobile Numbers</span>
            {showAdditionalMobiles ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </button>
          
          {showAdditionalMobiles && (
            <div className="space-y-3 pl-6 border-l-2 border-blue-100">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Mobile Number 2</label>
                <input
                  type="tel"
                  name="mobile2"
                  value={formData.mobile2}
                  onChange={onChange}
                  onFocus={() => onFocus('mobile2')}
                  onBlur={() => onBlur('mobile2')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={getPlaceholder('mobile2', 'Enter alternate mobile number (optional)')}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Mobile Number 3</label>
                <input
                  type="tel"
                  name="mobile3"
                  value={formData.mobile3}
                  onChange={onChange}
                  onFocus={() => onFocus('mobile3')}
                  onBlur={() => onBlur('mobile3')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={getPlaceholder('mobile3', 'Enter another mobile number (optional)')}
                />
              </div>
            </div>
          )}
        </div>

        {/* QR Code Scanner */}
        <div>
          <button
            type="button"
            onClick={onStartQRScanner}
            className="w-full flex items-center justify-center space-x-2 bg-gray-600 text-white py-3 rounded-lg hover:bg-gray-700 transition-colors"
            title="Scan any QR Code"
          >
            <QrCode className="w-5 h-5" />
            <span>Scan QR Code</span>
          </button>
        </div>

        {/* QR Code Data Display (if scanned) */}
        {formData.qrCodeData && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                <Scan className="w-5 h-5 text-green-600" />
                <h4 className="text-sm font-medium text-green-800">QR Code Data</h4>
              </div>
              <button
                onClick={onClearQRData}
                className="text-xs text-red-600 hover:text-red-700 transition-colors"
                title="Remove QR Code Data"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="bg-white border border-green-300 rounded p-3">
              <p className="text-sm text-gray-800 font-mono break-all">
                {formData.qrCodeData}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileQRSection;
